// i18n area 'apps' (task #127): sections apps, cts, ctTemplates, oci.
// Split out of the old monolithic dictionaries.ts by scripts/split-dictionaries.mjs;
// since the split, these files are the SOURCE — add and edit keys here. The script
// only re-runs to re-split a merged monolithic dictionaries.ts (see its header).

export const enUS = {

  // Apps wave 1 (Annex R): managed podman containers — worker-supervised on
  // one declared node; image = the Annex Q branch@digest lockfile with a
  // pinned/tracking policy; untagged L3 attachment with a static ip.
  apps: {
    title: 'Apps',
    create: 'Create app',
    createTitle: 'Create app',
    editTitle: 'Edit app',
    name: 'Name',
    nameHint: 'DNS-1123, unique within the scope — immutable after creation.',
    node: 'Node',
    nodeHint: 'The serving member node — explicit placement (a scheduler comes later).',
    image: 'Image',
    editImageHint: 'A new reference re-resolves through the anagraphics — the pin/rollback gesture.',
    imagePolicy: 'Policy',
    policyPinned: 'pinned',
    policyTracking: 'tracking',
    policyPinnedHint: 'The digest moves only on command.',
    policyTrackingHint: 'Rolls automatically when a refresh moves the branch.',
    archMovePolicy: 'Cross-arch move',
    archMoveRefuse: 'refuse',
    archMoveReresolve: 'reresolve',
    archMovePolicyHint: 'What happens if the app is moved to a node of another architecture: refuse refuses the move, reresolve re-resolves the lockfile from the branch on the new arch — the content changes, and the answer names the digest change.',
    network: 'Network',
    networkHint: 'Untagged L3 attachment: a fabric logical network (recommended — gateway/DNS from its single source), an overlay, or a raw host bridge (discouraged).',
    overlayPrefix: 'overlay:',
    fabricPrefix: 'fabric:',
    bridgePrefix: 'host bridge:',
    ipAssignment: 'IP assignment',
    ipStatic: 'Static',
    ipAuto: 'Automatic (IPAM)',
    ipAssignmentHint_static: 'You give the address now.',
    ipAssignmentHint_auto: 'The IPAM picks a free address from the network\'s TVirt reserved ranges.',
    bridgeDiscouraged: 'Raw host bridge — DISCOURAGED: TVirt knows nothing about its addressing, so gateway and DNS must be given by hand. The recommended way is a fabric logical network.',
    ip: 'IP',
    ipHint: 'Static IPv4 inside the attachment subnet.',
    ipCidrHint: 'CIDR form on a raw bridge (e.g. 192.168.1.10/24) — nothing to derive the prefix from.',
    gateway: 'Gateway',
    gatewayManualHint: 'Manual — a raw bridge derives nothing.',
    dnsServers: 'DNS servers',
    dnsManualHint: 'Manual, comma-separated — at least one.',
    rootKind: 'Root',
    rootEphemeral: 'ephemeral',
    rootPersistent: 'persistent',
    rootEphemeralHint: 'Recreated from the image at every re-creation; lives on the cluster ephemeral storage.',
    rootEphemeralHintNamed: 'Recreated from the image at every re-creation; lives on the cluster ephemeral storage ({name}).',
    rootPersistentHint: 'Survives restarts; lives on the storage you pick.',
    rootStorage: 'Root storage',
    editRootStorageHint: 'The root re-renders on the new pool — existing data does NOT move by itself.',
    volumes: 'Volumes',
    volumeAdd: 'Add volume',
    volumeName: 'Name',
    volumeMountPath: 'Mount path',
    volumeKind: 'Kind',
    volumeStorage: 'Storage',
    devices: 'Devices',
    deviceClassHint: 'You require a CLASS (a gpu-device or usb-device tag), never a particular device: the declared node must carry one marked for the Apps. Several Apps can use the same device — a container shares it, it does not take it away from the host.',
    deviceSlot: 'Slot',
    deviceTag: 'Tag',
    deviceValue: 'Class',
    deviceUnresolved: 'nothing resolves right now',
    noDeviceTags: 'No gpu-device or usb-device tag yet: create one and assign it to the devices marked for the Apps.',
    env: 'Environment',
    envHint: 'One KEY=value per line.',
    secretEnvTitle: 'Secret variables',
    secretEnvNotice: 'A secret value never comes back from the API: nobody reads it again, not even whoever wrote it. It is sealed inside the Checkpoint with the masterpass, so a restore without the masterpass will require typing it again.',
    secretEnvPromote: 'Make one of the variables written above secret:',
    secretEnvAdd: 'Add secret variable',
    secretEnvName: 'Name',
    secretEnvValue: 'Value',
    secretEnvKept: 'kept — leave empty to keep it as it is',
    secretEnvWithdrawWarning: 'To stop treating an existing variable as secret, type a new value for it: withdrawing the mark on its own would be a way to read what never leaves the store. Removing it deletes the value.',
    secretEnvChip: 'secret',
    startNow: 'Start immediately (the app is otherwise created stopped)',
    deletePersistentIntro: 'This app has persistent data — choose what happens to it:',
    deleteKeepData: 'Keep the data (the directories stay on their storages, orphaned)',
    deletePurgeData: 'Also delete the data',
    phase: 'Phase',
    phase_running: 'Running',
    phase_stopped: 'Stopped',
    phase_error: 'Error',
    phase_unknown: 'Unknown',
    desiredState: 'Wanted',
    wantedRunning: 'running',
    wantedStopped: 'stopped',
    start: 'Start',
    stop: 'Stop',
    restart: 'Restart',
    refreshImage: 'Refresh image (adopt the new @current)',
    logs: 'Logs',
    logsTitle: 'Logs',
    logsEmpty: 'No output captured.',
    shell: 'Shell',
    shellTitle: 'Shell',
    shellUnavailable: 'The App is not running — a shell needs a live container. Its logs are readable in any state.',
    shellConnect: 'Connect',
    shellDisconnect: 'Disconnect',
    shellExited: 'The session ended with exit code {code}.',
    shellEndedWithoutStatus: 'The session ended without an exit status: {reason}',
    shellRevoked: 'The session ticket is no longer valid — it expired, or the login that opened it was revoked.',
    shellHint: 'Click the black area and type: the keys travel to the container. Ctrl+C and the arrows work; exit closes the shell.',
    shellStatus: {
      connecting: 'Connecting…',
      connected: 'Connected',
      ended: 'Ended'
    },
    deleteRunningError: '{name} is wanted running — stop it first.',

    // Dialog standard (round #156): the detail tabs and the missing contract
    // fields — every App field visible, locks with their why.
    tabs: {
      general: 'General',
      image: 'Image',
      network: 'Network',
      storage: 'Storage',
      devices: 'Devices',
      observed: 'Observed'
    },
    attachment: 'Attachment',
    runningDigest: 'Running digest',
    lastError: 'Last error',
    reportObservedAt: 'Last worker report',
    desiredStateLock: 'Owned by the Start/Stop actions — never a direct edit.',
    ipEditDisabledHint: 'Editable only with a static assignment.',
    derivedFromNetworkHint: "Derived from the network's single source — manual only on a raw bridge with a static IP.",
    ephemeralUnset: 'not configured',
    ephemeralMissing: 'The App ephemeral storage is not configured — creating an app with any ephemeral piece (root or volume) is refused until it is set.',
    ephemeralCurrent: 'App ephemeral storage: {name}',
    ephemeralMissingCreate: 'This app has ephemeral pieces but the App ephemeral storage of the cluster is not set — the create will be refused. Set it from the banner of the Apps page first.',
    setEphemeral: 'Set ephemeral storage',
    changeEphemeral: 'Change ephemeral storage',
    setEphemeralTitle: 'Set the App ephemeral storage',
    changeEphemeralTitle: 'Change the App ephemeral storage',
    ephemeralStorageHint: 'Cluster-wide DEFAULT: the dir-like pool where the ephemeral roots and ephemeral volumes of every app live, unless the app declares its own override. A change is not hot and restarts nothing: it applies at each app\'s next start — ephemeral data does not move.',
    ephemeralPool: 'Storage pool',
    ephemeralOverride: 'Ephemeral storage (per-app override)',
    ephemeralOverrideInherit: 'Inherit the cluster default',
    ephemeralOverrideInheritNamed: 'Inherit the cluster default ({name})',
    ephemeralOverrideHint: 'Per-app pool for the ephemeral root and volumes — leave empty to inherit the cluster default. Changing it is not hot and restarts nothing: it applies at the next start (ephemeral data reborn empty on the new pool) and requires the app.ephemeralStorage.set permission.',
    ephemeralOverrideTag: 'override',

    imageAnyBranch: 'All branches',
    imageBranchHint: 'Narrows the images to those this branch names; with no branch you see every image of the repository.',
    imageDigestHint: 'The content: chosen from the table of digests, which carries its provenance and its architecture. The same branch offers one per architecture, and they are not interchangeable.',
    imageLockfile: 'Resolved reference',
    imageNoImages: 'No image in the cache for this repository — pull one from OCI images.',
    imageHeadNotCached: 'not in cache — pulled at start',
    imageNoRegistries: 'No repository registered: register one to be able to choose an image.',
    imageChosenHint: 'The image is chosen from the cascade in the edit dialog: the reference is never typed by hand.',
    registerRepositoryHint: 'Registering downloads nothing: it makes a repository observable, so the image can be chosen without leaving this dialog.',
    registerRepositoryBranchHint: 'Optional: the first branch to observe (e.g. latest).',
    registerRepositoryElsewhere: 'The repository you are looking for is not registered: registering happens in the OCI images section, by whoever holds the permission.'
  },

  // CT Templates (round K2 #63): the MATERIALIZED CT objects — import from
  // catalog is the only entrance; files are uploaded inside catalogs.
  cts: {
    createAction: 'Create CT',
    createTitle: 'Create a container from a template',
    createError: 'The container could not be created',
    template: 'CT template',
    noTemplates: 'No CT templates yet — import one under CT templates',
    resources: 'Resources',
    node: 'Node',
    phase: {
      defined: 'Never powered on',
      materializing: 'Materializing',
      ready: 'Ready',
      starting: 'Starting',
      running: 'Running',
      stopping: 'Stopping',
      stopped: 'Stopped',
      deleting: 'Deleting',
      error: 'Error'
    },
    actions: {
      start: 'Start',
      stop: 'Stop',
      restart: 'Restart',
      markTemplate: 'Promote to CT template',
      error: 'The action was refused'
    },
    markTemplate: {
      confirmTitle: "Promote '{name}' to CT template?",
      confirmBody: 'The container leaves this list and joins CT templates, ready to spawn new CTs at once. Its metadata stay editable, and you can turn it back into a CT at any time.',
      confirm: 'Promote',
      moved: "'{name}' is now a CT template — find it under CT templates."
    },
    stopCause: {
      api: 'Stopped via TVirt',
      'ct-exit': 'The container exited on its own',
      'host-reboot': 'Died with a host reboot',
      'start-failed': 'The start failed — the task error says what is missing and how to fix it'
    },

    // Round #347: the two ways to look inside a CT. They are complementary —
    // the console needs a live container, the logs matter most when there
    // isn't one — and the refusals say so instead of just greying out.
    console: 'Console',
    consoleUnavailable: 'The container is not running — a console needs a live container. Its logs are readable in any state.',
    consoleNoTemplate: 'A template never runs, so it has no console: create a CT from it and open that one.',
    consoleConnect: 'Connect',
    consoleDisconnect: 'Disconnect',
    consoleReconnect: 'Reconnect',
    consoleTakenBy: 'Another viewer took this console ({user}). One console per container: reconnecting takes it back.',
    consoleHint: 'Click the black area and type: the keys travel to the container. Ctrl+C and the arrows work.',
    consoleEndpointDown: 'The console endpoint cannot be opened on the node serving the container: {cause}',
    consoleStatus: {
      connecting: 'Connecting…',
      connected: 'Connected',
      reconnecting: 'Reconnecting…'
    },

    // T157-P8 f: the shell (lxc-attach) and the birth credentials.
    shell: 'Shell',
    shellTitle: 'Shell',
    shellUnavailable: 'The container is not running — a shell needs a live container. Its logs are readable in any state.',
    shellNoTemplate: 'A template never runs, so it has no shell: create a CT from it and open that one.',
    shellConnect: 'Connect',
    shellDisconnect: 'Disconnect',
    shellExited: 'The session ended with exit code {code}.',
    shellEndedWithoutStatus: 'The session ended without an exit status: {reason}',
    shellRevoked: 'The session ticket is no longer valid — it expired, or the login that opened it was revoked.',
    shellHint: 'Click the black area and type: the keys travel to the container. Ctrl+C and the arrows work; exit closes the shell.',
    shellStatus: {
      connecting: 'Connecting…',
      connected: 'Connected',
      ended: 'Ended'
    },
    rootPassword: 'Root password',
    rootPasswordHint: 'Written once into the copied rootfs (hashed, never shown again). The distributions\' images ship no root password: without one, the shell is the only way in.',
    sshKeys: 'SSH public keys for root',
    sshKeysHint: 'One OpenSSH public key per line, into root/.ssh/authorized_keys.',
    logs: 'Logs',
    logsTitle: 'Logs',
    logsEmpty: 'No output captured.',
    logsSource: 'Which log',
    logsSourceConsole: "The container's init console — why the service inside did not start",
    logsSourceSupervisor: 'The liblxc supervisor — why the container never got as far as running',
    logsNeverStarted: 'Never started, so there is nothing logged yet — start it and the logs will be there even if the start fails.',
    logsNoTemplate: 'A template never runs, so it never logged.',
    logsError: 'The logs could not be captured',

    // Dialog standard (round #156): the detail tabs shared by the CT and CT
    // Template lists, plus the locks with their why.
    tabs: {
      general: 'General',
      hardware: 'Hardware',
      network: 'Network',
      provenance: 'Provenance'
    },
    templateFlag: 'CT template',
    templateLock: 'Changes only through the Promote / Turn back actions — never a direct edit.',
    sourceLock: 'Provenance, recorded at birth — never rewritten; a dead source leaves the bare reference.',
    storageLock: 'Fixed at creation — moving the rootfs is a future round.',
    startedAt: 'Started',
    lastStop: 'Last stop',
    editNicsTitle: 'Edit networks',
    nicDisconnected: 'disconnected'
  },
  ctTemplates: {
    title: 'CT Templates',
    importAction: 'Import from catalog',
    importTitle: 'Import CT template from catalog',
    importSubmit: 'Import',
    importError: 'The import was refused',
    importHint: 'Materializing copies the rootfs onto the chosen storage — the catalog file stays untouched. To add new files, upload them inside a catalog first.',
    sourceFile: 'CT template file',
    sourceFileHint: 'The ct-rootfs files of the chosen catalog, ready ones only',
    noFiles: 'No ready ct-rootfs file in this catalog — upload one from the catalog page first',
    source: 'Source file',
    rootfsStorage: 'Rootfs storage',
    rootfsStorageHint: 'Where the extracted rootfs tree lives — a filesystem-backed storage with enough free space for the extracted size plus about 1 GiB of headroom (the import fails otherwise).',
    resources: 'Resources',
    cores: 'vCPU',
    memoryMiB: 'Memory (MiB)',
    cpuLimit: 'CPU limit',
    cpuLimitHint: 'Host-core equivalents (e.g. 1.5); empty = uncapped',
    cpuUncapped: 'uncapped',
    cpuWeight: 'CPU weight',
    hostname: 'Hostname',
    hostnameHint: 'Empty = the CT\'s own name',
    nameservers: 'DNS nameservers',
    dnsHint: 'Comma-separated; empty = inherit the host\'s',
    searchDomains: 'DNS search domains',
    dnsInherited: 'Inherited from the host',
    networks: 'Networks',
    nicSlot: 'NIC slot',
    phase: {
      materializing: 'Materializing',
      ready: 'Ready',
      deleting: 'Deleting',
      error: 'Error'
    },
    exportAction: 'Export to catalog',
    exportTitle: 'Export to catalog',
    exportSubmit: 'Export',
    exportError: 'The export was refused',
    exportHint: 'Packs the materialized rootfs into a NEW ct-rootfs file in the chosen catalog — the template stays untouched.',
    exportName: 'File name',
    exportNameHint: 'Empty = <template>-export-<n>',
    markCt: {
      action: 'Turn back into CT',
      confirmTitle: "Turn '{name}' back into a CT?",
      confirmBody: 'The template leaves this list and joins the CT list, startable again. Until you promote it back, no new CT can be created from it.',
      confirm: 'Turn back',
      moved: "'{name}' is a plain CT again — find it under CT."
    }
  },

  // OCI wave 1 (Annex Q — the tag-as-branch model): an OCI tag is a branch
  // (mobile pointer), the digest is the commit; refresh = "git fetch",
  // pull = resolve NOW, download THAT digest.
  oci: {
    registriesTitle: 'OCI registries',
    imagesTitle: 'OCI images',
    provider: 'Provider',
    providerDockerhub: 'Docker Hub',
    providerGhcr: 'GHCR',
    providerEcr: 'Amazon ECR',
    providerQuay: 'Red Hat Quay',
    providerGeneric: 'Generic',
    providerImmutableHint: 'The provider is immutable after creation — a different provider is a new registry.',
    url: 'URL',
    urlHintPreset: 'Preset by the provider — override only for mirrors.',
    urlHintRequired: 'Required for generic registries: the https:// base URL.',
    urlHintEcr: 'Required: ECR endpoints are per-account/per-region — there is no preset.',
    credential: 'Credential',
    credentialNone: 'Anonymous access',
    credentialHint: 'A username-and-password keystore entry; workers resolve it at use time — never per-node auth files.',
    nameHint: 'Host-like name (docker.io, registry.intra); unique per scope, immutable',
    addRegistry: 'Add registry',
    addRegistryTitle: 'Add OCI registry',
    verify: 'Verify',
    verifyState: 'Verification',
    verified: 'Verified',
    verifyFailed: 'Failed',
    neverVerified: 'Never verified',
    registryInUseError: "'{name}' still has registered repositories — delete them first.",
    repositories: 'Repositories',
    repositoriesHint: 'Registering observes branches (OCI tags) — nothing downloads. Refresh resolves every observed branch and keeps the reflog.',
    noRepositories: 'No repositories registered on this registry.',
    registerRepository: 'Register repository',
    repoPath: 'Path',
    repoPathHint: "Repository path on the registry, e.g. 'example' or 'library/nginx'",
    branches: 'Branches',
    branchesHint: 'Observed branches (OCI tags), comma-separated — e.g. 1.1.0, 1.1.1',
    branch: 'Branch',
    branchPullHint: 'Resolved NOW: the pull downloads the digest the branch points at today.',
    refresh: 'Refresh',
    deleteRepository: 'Delete repository',
    reflogCount: '{n} previous',
    noResolutions: 'Never refreshed — no resolutions observed yet.',
    refreshFailed: 'Last refresh failed ({when})',
    loadError: 'Could not load the repositories.',
    reference: 'Reference',
    digest: 'Digest',
    digestHint: 'sha256:… — the exact image, no branch involved (detached)',
    size: 'Size',
    nodes: 'Nodes',
    detached: 'detached',
    detachedHint: 'Pulled by digest, without a branch — the drift check does not apply.',
    pulledAt: 'Pulled',
    pull: 'Pull',
    pullTitle: 'Pull image',
    pullHint: 'Pre-warming, never a prerequisite: a branch pull resolves NOW and downloads THAT digest; a digest pull is detached.',
    pullError: 'The pull was refused',
    pullMode: 'Pull by',
    pullModeBranch: 'Branch (resolve now)',
    pullModeDigest: 'Digest (detached)',
    pullModeBranchHint: 'The honest default: like apt install, re-running later may give a different image.',
    pullModeDigestHint: 'Exact digest — reproducibility and rollback; forgoes the drift check.',

    // The two-button import pattern (#88, ratifica 2026-07-30 sera): "from
    // registry" is the existing guided pull, "from catalog" materializes a
    // ready oci-image archive (the air-gapped path).
    importFromRegistry: 'Import from registry',
    importFromCatalog: 'Import from catalog',
    importCatalogTitle: 'Import an OCI image from a catalog',
    importCatalogHint: 'The air-gapped path: a ready oci-image archive (tar/tar.gz — docker-archive or oci-archive) is materialized into the image store with the reference catalog/<catalog>/<file>@<digest>; apps consume it like any other image. Archives are uploaded in the catalogs — this section only imports.',
    importCatalogFile: 'Archive',
    importCatalogFileHint: 'The ready oci-image files of the chosen catalog.',
    importCatalogNoFiles: 'No ready oci-image archive in this catalog — upload one from the catalog detail.',
    importCatalogError: 'The import was refused',
    registry: 'Registry',
    repository: 'Repository',
    cacheMissing: 'The cluster OCI cache is not configured — pulls are refused until a storage pool is set.',
    setCache: 'Set cache',
    setCacheTitle: 'Configure the OCI cache',
    setCacheHint: 'The cluster-wide DEFAULT pool hosting the downloaded image blobs — a scope may override it with its own pool (Scopes page). Changing it is not hot and restarts nothing, and never moves blobs: they reappear lazily on the next start/use.',
    cachePool: 'Storage pool',
    changeCache: 'Change cache',
    changeCacheTitle: 'Change the OCI cache pool',
    cacheCurrent: 'Cluster OCI cache (default): {name}',
    sectionOverview: 'Overview',
    sectionRepositories: 'Repositories',
    sectionExplore: 'Explore',
    exploreHint: 'A live remote search on the registry — Docker Hub full (description, pulls, stars), other providers best-effort. Select a result to read its full description and browse its remote tags; from a tag you can add the reference to a TVirt repository.',
    exploreSearch: 'Search',
    exploreUnsupported: 'This provider does not support searching.',
    exploreNoResult: 'No repositories matched.',
    exploreNoTags: 'No tags matched on this repository.',
    exploreOfficial: 'Official',
    explorePulls: 'Pulls',
    exploreStars: 'Stars',
    exploreUpdated: 'Updated {when}',
    exploreLoadMore: 'Load more',
    exploreSelectHint: 'Select a result: its full description and remote tags load right here.',
    exploreNoDescription: 'No description available from this provider.',
    exploreTagsTitle: 'Remote tags',
    exploreTagFilter: 'Search tags',
    exploreAddToRepo: 'Add to repository',
    exploreAdded: '{path}:{tag} is now tracked — see the Repositories section.',
    addBranch: 'Add branch',
    purgeRequiredTitle: "Delete '{name}' and its images?",
    purgeRequiredBody: 'No app consumes this repository, but downloaded images still exist: deleting it also removes their records, and the cached blobs go to garbage collection.',
    purgeConfirm: 'Delete the images too',
    providerSelectHint: 'Select the registry type: the card tells the preset and the use.',
    providerDesc: {
      dockerhub: 'The public Docker registry: preset URL, full search with descriptions and counts.',
      ghcr: 'GitHub Container Registry: preset URL; accounts use a personal access token.',
      ecr: 'Amazon Elastic Container Registry: per-account/per-region endpoints — the URL must be given.',
      quay: 'The Red Hat Quay registry: preset URL.',
      generic: 'Self-hosted registry (Harbor, registry:2, …): the https:// URL is required.'
    },

    // Dialog standard (round #156): the image/registry detail fields the
    // columns never showed.
    layerCount: 'Layers',
    verifiedAt: 'Last verification',
    verifyOk: 'Outcome',
    verifyMessage: 'Verification message',

    // The #348/R39 model: the image IS the digest — the content — and registry, repository and branch are the NAMES that reach it.
    head: 'Head',
    heads: 'Heads by architecture',
    listDigest: 'List digest',
    neverResolved: 'Never resolved',
    totalSize: 'Total size',
    reflogTitle: 'Resolution history',
    usedBy: 'Used by',
    usageLockfile: 'lockfile',
    usageRunning: 'running',
    evictable: 'evictable',
    evict: 'Evict from the cache',
    evictHeadTitle: 'Evict {digest}?',
    evictHeadBody: 'It is the current resolution of at least one collector: evicting it is legitimate — it is only cache, the remote branch still serves it — but it must never happen by mistake. The blob is ONE: the eviction empties every collector sharing it.',
    evictConfirm: 'Evict it anyway',

    arch: 'Architecture',
    names: 'Names',
    noNames: 'no name',
    views: 'Views',
    lastView: 'last view',
    evictLastViewTitle: 'Remove the last reference to {digest}?',
    evictLastViewBody: "This is the last view on this content: dropping it makes the image cease to exist on TVirt and frees the bytes ({size}). It is the model's docker image rm — not a side effect, the operation itself.",
    branchesTabHint: "The observed branches of this repository: for each one today's heads — one per architecture — the chronology of the refreshes and how many of the images it named are in the cache. Refresh is the repository's «git fetch»: it resolves all its branches in one go.",
    noBranches: 'No branch observed on this repository.',
    stopObserving: 'Stop observing the branch',
    pullBranch: 'Pull the branch head into the cache (the refresh resolves, this one downloads)',
    pullDigest: 'Pull this digest into the cache',
    imageCount: 'Images',
    deleteBranchPurgeTitle: 'Leave the images of {name} without a name?',
    deleteBranchPurgeBody: "No app names this branch, but some images are reached by it alone: deleting it leaves them in the cache with no name, listed among the repository's images and evictable by hand.",
    deleteBranchPurgeConfirm: 'Delete and leave the images unnamed',
    imagesTabHint: 'The images of this repository — the contents, that is the digests — visible without picking a branch too. An image is unique per cluster: the names that reach it may be more than one.',
    noImages: 'No image of this repository is materialized in the cache.',
    deleteRepositoryTitle: 'Delete {name}?',
    deleteRepositoryBody: 'The repository and its branches go, with their chronology. The images stay in the cache: they only lose this name. A repository an app names cannot be deleted.',
    provenanceCatalog: 'catalog',
    refreshedAt: 'Refreshed',
    neverRefreshed: 'Never refreshed',
    sectionBranches: 'Branches',
    sectionImages: 'Images',
    repositoriesGovernedElsewhere: 'Repositories are only looked at here: registering them, refreshing them, observing their branches and evicting their images all happen in the OCI images section.',
    gotoImages: 'Go to OCI images'
  },

  // Pod nativi (P1): the Pod list — the template's census of instances, the
  // whole-Pod lifecycle, the scale and the delete.
  pods: {
    title: 'Pods',
    containers: 'Containers',
    instances: 'Instances',
    instancesTooltip: '{running} running, {error} in error',
    placement: 'Placement',
    spread: 'spread',
    ha: 'HA',
    templateRevision: 'revision',
    deleting: 'Deleting',
    empty: 'No Pods — create an app (one container) or a Pod from the CLI (create-pod).',
    openInstances: 'Open the instances',
    rollingRestart: 'Rolling restart',
    scale: 'Scale',
    scaleTitle: 'Scale {name}',
    scaleHint: 'Number of instances (0–64): up fills the lowest free ordinal, down retires the highest and keeps its persistent data.',
    scaleError: 'Scaling failed',
    replicas: 'Replicas',
    deleteRunningError: '{name} is running — stop it first.',
    deleteInstancesIntro: 'All {count} instances will be removed.',
    stateRunning: 'Running',
    stateStopped: 'Stopped',
    editor: {
      createTitle: 'Create Pod',
      create: 'Create Pod',
      editTitle: 'Edit Pod',
      nameHint: 'DNS-1123, at most 58 characters — the instances are <name>-<index>; immutable after creation.',
      restartPolicy: 'Restart policy',
      restartPolicyHint_always: 'The node restarts any exited container, with an exponential backoff (10 s → 5 min).',
      restartPolicyHint_onFailure: 'Only a container that exits non-zero is restarted; exit 0 rests.',
      restartPolicyHint_never: 'An exited container is left as it is — the instance ends succeeded or failed.',
      grace: 'Grace (s)',
      graceHint: 'The SIGTERM → SIGKILL bound of a stop.',
      hostname: 'Hostname',
      hostnameHint: 'Empty = the instance name.',
      hostnameDefault: 'the instance name',
      sharePid: 'Share the PID namespace between the containers',
      containersIntro: 'Init containers run to completion, in order, before the containers start; the containers share the instance\'s network (one IP, localhost between them).',
      initContainers: 'Init containers',
      initAdd: 'Add init container',
      initHint: 'Each must exit 0; a failure lands the instance in error and the node retries it with backoff.',
      containerAdd: 'Add container',
      atLeastOne: 'A Pod needs at least one container.',
      duplicateNames: 'Container names must be unique within the Pod (init containers included).',
      containerName: 'Container name',
      containerInvalid: 'This container is incomplete: name (DNS-1123), image, mounts on declared volumes, numeric limits and probe.',
      command: 'Command',
      commandHint: 'Overrides the image entrypoint — one argument per line, never a shell string.',
      args: 'Arguments',
      argsHint: 'One per line (the image CMD when empty).',
      mounts: 'Mounts',
      mountAdd: 'Add mount',
      mountNoVolumes: 'Declare a volume in the Storage tab first.',
      readOnly: 'read-only',
      resources: 'Limits',
      cpus: 'CPUs',
      cpusHint: 'podman --cpus (e.g. 1.5)',
      memory: 'Memory (MiB)',
      memoryHint: 'podman --memory',
      health: 'Liveness probe',
      healthCmd: 'Probe command',
      healthCmdHint: 'Run inside the container by the node every supervision tick — one argument per line; exit 0 = healthy.',
      healthTimeout: 'Timeout (s)',
      healthRetries: 'Retries',
      healthRetriesHint: 'Consecutive failures before the node restarts the container.',
      ips: 'Static addresses',
      ipsHint: 'One per instance, in ordinal order — a scale beyond the list refuses.',
      instance: 'instance',
      volumesHint: 'Volumes are the Pod\'s; the mount path is each container\'s (Containers tab).',
      spread: 'Spread',
      spreadHint_preferred: 'Instances on different nodes when possible.',
      spreadHint_required: 'Instances on different nodes, or the scale refuses.',
      spreadHint_none: 'The decider alone.',
      ha: 'High availability (opt-in)',
      haHint: 'Every persistent piece on shared storage; if the node dies, the HA decider recreates the instances on another member (automatic failover, like the HA VMs).',
      yes: 'yes',
      no: 'no'
    },
    detail: {
      container: 'Container',
      actions: 'Actions',
      ready: 'Ready',
      notReady: 'Not ready',
      restarts: 'Restarts',
      lastRestartAt: 'Last restart:',
      backoffUntil: 'Retry at',
      stateAbsent: 'absent',
      stateRunning: 'running',
      stateTerminated: 'terminated',
      stateWaiting: 'waiting',
      init: 'init',
      noInstances: 'No instances (replicas 0).',
      ha: 'HA',
      haHint: 'Automatic failover: if the node dies, the HA decider recreates the instance on another member (every persistent piece on shared storage).',
      authority: 'Authority (node)',
      homeless: 'no authority',
      homelessHint: 'A checkpoint restore leaves the authority out (#380): no node serves it until you scale the Pod (even to the same number of replicas).',
      restartContainer: 'Restart this container alone (the pod stays up)',
      divergingHint: 'This instance\'s wanted state differs from the Pod\'s default (PD-P19).',
      revisionLag: 'revision {applied} applied of {current}',
      updatedInstances: '{updated}/{desired} instances at this revision'
    }
  }
}

export const itIT: typeof enUS = {

  // Apps wave 1 (Annex R): container podman gestiti — supervisionati dal
  // worker su un nodo dichiarato; immagine = lockfile branch@digest (Annex Q)
  // con policy pinned/tracking; attacco L3 untagged con IP statico.
  apps: {
    title: 'App',
    create: 'Crea app',
    createTitle: 'Crea app',
    editTitle: 'Modifica app',
    name: 'Nome',
    nameHint: 'DNS-1123, unico nello scope — immutabile dopo la creazione.',
    node: 'Nodo',
    nodeHint: 'Il nodo membro che la serve — placement esplicito (lo scheduler arriva dopo).',
    image: 'Immagine',
    editImageHint: "Un nuovo riferimento si ri-risolve tramite l'anagrafica — il gesto di pin/rollback.",
    imagePolicy: 'Policy',
    policyPinned: 'pinned',
    policyTracking: 'tracking',
    policyPinnedHint: 'Il digest si muove solo su comando.',
    policyTrackingHint: 'Rolling automatico quando un refresh muove il branch.',
    archMovePolicy: 'Spostamento cross-arch',
    archMoveRefuse: 'refuse',
    archMoveReresolve: 'reresolve',
    archMovePolicyHint: "Cosa succede se l'app viene spostata su un nodo di un'altra architettura: refuse rifiuta lo spostamento, reresolve ri-risolve il lockfile dal branch sull'arch nuova — il contenuto cambia, e la risposta nomina il cambio di digest.",
    network: 'Rete',
    networkHint: 'Attacco L3 untagged: una rete logica di fabric (consigliata — gateway/DNS dalla sua fonte unica), una overlay, o un bridge host nudo (sconsigliato).',
    overlayPrefix: 'overlay:',
    fabricPrefix: 'fabric:',
    bridgePrefix: 'bridge host:',
    ipAssignment: 'Assegnazione IP',
    ipStatic: 'Statico',
    ipAuto: 'Automatico (IPAM)',
    ipAssignmentHint_static: "L'indirizzo lo dai tu, ora.",
    ipAssignmentHint_auto: "L'IPAM sceglie un indirizzo libero dai range riservati a TVirt della rete.",
    bridgeDiscouraged: 'Bridge host nudo — SCONSIGLIATO: TVirt non sa nulla del suo indirizzamento, quindi gateway e DNS vanno dati a mano. La via consigliata è una rete logica di fabric.',
    ip: 'IP',
    ipHint: "IPv4 statico dentro la subnet dell'attacco.",
    ipCidrHint: 'In forma CIDR su un bridge nudo (es. 192.168.1.10/24) — non c\'è da dove derivare il prefisso.',
    gateway: 'Gateway',
    gatewayManualHint: 'Manuale — un bridge nudo non deriva nulla.',
    dnsServers: 'Server DNS',
    dnsManualHint: 'Manuali, separati da virgola — almeno uno.',
    rootKind: 'Root',
    rootEphemeral: 'effimera',
    rootPersistent: 'persistente',
    rootEphemeralHint: "Ricreata dall'immagine a ogni ricreazione; vive sullo storage effimero di cluster.",
    rootEphemeralHintNamed: "Ricreata dall'immagine a ogni ricreazione; vive sullo storage effimero di cluster ({name}).",
    rootPersistentHint: 'Sopravvive ai riavvii; vive sullo storage che scegli.',
    rootStorage: 'Storage root',
    editRootStorageHint: 'La root si ri-renderizza sul nuovo pool — i dati esistenti NON si spostano da soli.',
    volumes: 'Volumi',
    volumeAdd: 'Aggiungi volume',
    volumeName: 'Nome',
    volumeMountPath: 'Percorso di mount',
    volumeKind: 'Tipo',
    volumeStorage: 'Storage',

    // Round #330 — i device dell'App, per CLASSE. Il testo dice la cosa che
    // distingue le App dalle VM, perche' e' l'unica che sorprende: qui non si
    // prenota niente, si condivide.
    devices: 'Dispositivi',
    deviceClassHint: "Si chiede una CLASSE (un tag gpu-device o usb-device), mai un dispositivo preciso: il nodo dichiarato deve averne uno marcato per le App. Piu' App possono usare lo stesso dispositivo — un container lo condivide, non lo sottrae all'host.",
    deviceSlot: 'Slot',
    deviceTag: 'Tag',
    deviceValue: 'Classe',
    deviceUnresolved: 'nessun dispositivo risolto adesso',
    noDeviceTags: 'Nessun tag di kind gpu-device o usb-device: creane uno e assegnalo ai dispositivi marcati per le App.',
    env: 'Ambiente',
    envHint: 'Una CHIAVE=valore per riga.',
    secretEnvTitle: 'Variabili segrete',
    secretEnvNotice: 'Un valore segreto non torna piu\u2019 indietro dall\u2019API: nessuno lo rilegge, nemmeno chi l\u2019ha scritto. Nel Checkpoint viaggia sigillato con la masterpass, quindi un ripristino senza masterpass chiedera\u2019 di riscriverlo.',
    secretEnvPromote: 'Rendi segreta una delle variabili scritte qui sopra:',
    secretEnvAdd: 'Aggiungi variabile segreta',
    secretEnvName: 'Nome',
    secretEnvValue: 'Valore',
    secretEnvKept: 'conservato — lascialo vuoto per non cambiarlo',
    secretEnvWithdrawWarning: 'Per smettere di trattare come segreta una variabile che gia\u2019 lo e\u2019, scrivile un valore nuovo: togliere il marcatore da solo sarebbe un modo di LEGGERE cio\u2019 che non esce dallo store. Rimuoverla, invece, la cancella.',
    secretEnvChip: 'segreto',
    startNow: "Avvia subito (altrimenti l'app nasce ferma)",
    deletePersistentIntro: 'Questa app ha dati persistenti — scegli cosa farne:',
    deleteKeepData: 'Mantieni i dati (le directory restano sui loro storage, orfane)',
    deletePurgeData: 'Elimina anche i dati',
    phase: 'Fase',
    phase_running: 'In esecuzione',
    phase_stopped: 'Ferma',
    phase_error: 'Errore',
    phase_unknown: 'Sconosciuta',
    desiredState: 'Voluto',
    wantedRunning: 'in esecuzione',
    wantedStopped: 'ferma',
    start: 'Avvia',
    stop: 'Ferma',
    restart: 'Riavvia',
    refreshImage: 'Aggiorna immagine (adotta il nuovo @current)',
    logs: 'Log',
    logsTitle: 'Log',
    logsEmpty: 'Nessun output catturato.',
    shell: 'Shell',
    shellTitle: 'Shell',
    shellUnavailable: "L'App non è in esecuzione — una shell ha bisogno di un container vivo. I suoi log si leggono in qualunque stato.",
    shellConnect: 'Connetti',
    shellDisconnect: 'Disconnetti',
    shellExited: 'La sessione è terminata con codice di uscita {code}.',
    shellEndedWithoutStatus: 'La sessione è terminata senza uno stato di uscita: {reason}',
    shellRevoked: 'Il ticket della sessione non è più valido — è scaduto, oppure il login che lo aveva aperto è stato revocato.',
    shellHint: 'Clicca il riquadro nero e scrivi: i tasti arrivano al container. Ctrl+C e le frecce funzionano; exit chiude la shell.',
    shellStatus: {
      connecting: 'Connessione…',
      connected: 'Connessa',
      ended: 'Terminata'
    },
    deleteRunningError: '{name} è voluta in esecuzione — fermala prima.',

    // Dialog standard (round #156): i tab del dettaglio e i campi contract
    // mancanti — ogni campo dell'App visibile, lucchetti col perché.
    tabs: {
      general: 'Generale',
      image: 'Immagine',
      network: 'Rete',
      storage: 'Storage',
      devices: 'Dispositivi',
      observed: 'Osservato'
    },
    attachment: 'Attacco',
    runningDigest: 'Digest in esecuzione',
    lastError: 'Ultimo errore',
    reportObservedAt: 'Ultimo report del worker',
    desiredStateLock: 'Lo governano le azioni Avvia/Ferma — mai una modifica diretta.',
    ipEditDisabledHint: 'Modificabile solo con assegnazione statica.',
    derivedFromNetworkHint: 'Derivato dalla fonte unica della rete — manuale solo su bridge nudo con IP statico.',
    ephemeralUnset: 'non configurato',
    ephemeralMissing: "Lo storage effimero delle App non è configurato — creare un'app con pezzi effimeri (root o volumi) è rifiutato finché non lo imposti.",
    ephemeralCurrent: 'Storage effimero delle App: {name}',
    ephemeralMissingCreate: "Quest'app ha pezzi effimeri ma lo storage effimero delle App non è impostato — la creazione verrà rifiutata. Impostalo prima dal banner della pagina App.",
    setEphemeral: 'Imposta storage effimero',
    changeEphemeral: 'Cambia storage effimero',
    setEphemeralTitle: 'Imposta lo storage effimero delle App',
    changeEphemeralTitle: 'Cambia lo storage effimero delle App',
    ephemeralStorageHint: "DEFAULT di cluster: il pool dir-like dove vivono le root e i volumi effimeri di ogni app, salvo override dichiarato dall'app. Un cambio non è a caldo e non riavvia nulla: vale dal prossimo start di ciascuna app — i dati effimeri non si spostano.",
    ephemeralPool: 'Pool di storage',
    ephemeralOverride: 'Storage effimero (override per-app)',
    ephemeralOverrideInherit: 'Eredita il default di cluster',
    ephemeralOverrideInheritNamed: 'Eredita il default di cluster ({name})',
    ephemeralOverrideHint: "Pool per-app per root e volumi effimeri — lascia vuoto per ereditare il default di cluster. Cambiarlo non è a caldo e non riavvia nulla: vale dal prossimo start (dati effimeri rinati vuoti sul nuovo pool) e richiede il permesso app.ephemeralStorage.set.",
    ephemeralOverrideTag: 'override',

    imageAnyBranch: 'Tutti i branch',
    imageBranchHint: 'Restringe le immagini a quelle nominate da questo branch; senza branch si vedono tutte quelle del repository.',
    imageDigestHint: 'Il contenuto: si sceglie dalla tabella dei digest, che ne porta la provenienza e l’architettura. Lo stesso branch ne offre una per architettura, e non sono intercambiabili.',
    imageLockfile: 'Riferimento risolto',
    imageNoImages: 'Nessuna immagine in cache per questo repository — scaricane una da Immagini OCI.',
    imageHeadNotCached: "non in cache — scaricata all'avvio",
    imageNoRegistries: 'Nessun repository registrato: registrane uno per poter scegliere un’immagine.',
    imageChosenHint: 'L’immagine si sceglie dalla cascata nel dialog di modifica: il riferimento non si scrive a mano.',
    registerRepositoryHint: 'Registrare non scarica niente: rende osservabile un repository, così l’immagine si può scegliere senza uscire da qui.',
    registerRepositoryBranchHint: 'Facoltativo: il primo branch da osservare (es. latest).',
    registerRepositoryElsewhere: 'Il repository che cerchi non è registrato: la registrazione si fa nella sezione Immagini OCI, da chi ne ha il permesso.'
  },

  // Template CT (round K2 #63): gli oggetti CT MATERIALIZZATI — l'import da
  // catalogo è l'unico ingresso; i file si caricano dentro i cataloghi.
  cts: {
    createAction: 'Crea CT',
    createTitle: 'Crea un container da un template',
    createError: 'Il container non è stato creato',
    template: 'Template CT',
    noTemplates: 'Nessun template CT — importane uno da Template CT',
    resources: 'Risorse',
    node: 'Nodo',
    phase: {
      defined: 'Mai acceso',
      materializing: 'In materializzazione',
      ready: 'Pronto',
      starting: 'In avvio',
      running: 'In esecuzione',
      stopping: 'In arresto',
      stopped: 'Arrestato',
      deleting: 'In eliminazione',
      error: 'In errore'
    },
    actions: {
      start: 'Avvia',
      stop: 'Arresta',
      restart: 'Riavvia',
      markTemplate: 'Promuovi a template CT',
      error: "L'azione è stata rifiutata"
    },
    markTemplate: {
      confirmTitle: "Promuovere '{name}' a template CT?",
      confirmBody: 'Il container lascia questa lista e passa tra i Template CT, subito pronto a generare nuovi CT. I metadata restano modificabili, e puoi riportarlo a CT in ogni momento.',
      confirm: 'Promuovi',
      moved: "'{name}' ora è un template CT — lo trovi in Template CT."
    },
    stopCause: {
      api: 'Arrestato da TVirt',
      'ct-exit': 'Il container è uscito da solo',
      'host-reboot': 'Morto col riavvio del nodo',
      'start-failed': "L'avvio è fallito — l'errore del task dice cosa manca e come si sistema"
    },

    // Round #347: i due modi di guardare dentro un CT. Sono complementari —
    // la console vuole un container vivo, i log servono soprattutto quando
    // non c'è — e i rifiuti lo DICONO invece di limitarsi a spegnersi.
    console: 'Console',
    consoleUnavailable: 'Il container non è in esecuzione — una console ha bisogno di un container vivo. I suoi log invece si leggono in qualunque stato.',
    consoleNoTemplate: 'Un template non gira mai, quindi non ha console: creane un CT e apri quella.',
    consoleConnect: 'Connetti',
    consoleDisconnect: 'Disconnetti',
    consoleReconnect: 'Riconnetti',
    consoleTakenBy: 'Un altro operatore ha preso questa console ({user}). Una console per container: riconnettersi te la riprende.',
    consoleHint: "Clicca il riquadro nero e scrivi: i tasti arrivano al container. Ctrl+C e le frecce funzionano.",
    consoleEndpointDown: 'Il punto di accesso della console non si apre sul nodo che serve il container: {cause}',
    consoleStatus: {
      connecting: 'Connessione…',
      connected: 'Connessa',
      reconnecting: 'Riconnessione…'
    },

    // T157-P8 f: la shell (lxc-attach) e le credenziali di nascita.
    shell: 'Shell',
    shellTitle: 'Shell',
    shellUnavailable: 'Il container non è in esecuzione — una shell ha bisogno di un container vivo. I suoi log si leggono in qualunque stato.',
    shellNoTemplate: 'Un template non gira mai, quindi non ha shell: creane un CT e apri quella.',
    shellConnect: 'Connetti',
    shellDisconnect: 'Disconnetti',
    shellExited: 'La sessione è terminata con codice di uscita {code}.',
    shellEndedWithoutStatus: 'La sessione è terminata senza uno stato di uscita: {reason}',
    shellRevoked: 'Il ticket della sessione non è più valido — è scaduto, oppure il login che lo aveva aperto è stato revocato.',
    shellHint: 'Clicca il riquadro nero e scrivi: i tasti arrivano al container. Ctrl+C e le frecce funzionano; exit chiude la shell.',
    shellStatus: {
      connecting: 'Connessione…',
      connected: 'Connessa',
      ended: 'Terminata'
    },
    rootPassword: 'Password di root',
    rootPasswordHint: "Scritta una volta nel rootfs copiato (hash, mai più mostrata). Le immagini delle distribuzioni non hanno password di root: senza, la shell è l'unica via d'accesso.",
    sshKeys: 'Chiavi SSH pubbliche di root',
    sshKeysHint: 'Una chiave pubblica OpenSSH per riga, in root/.ssh/authorized_keys.',
    logs: 'Log',
    logsTitle: 'Log',
    logsEmpty: 'Nessun output catturato.',
    logsSource: 'Quale log',
    logsSourceConsole: "La console dell'init del container — perché il servizio dentro non è partito",
    logsSourceSupervisor: 'Il supervisore liblxc — perché il container non è mai arrivato a partire',
    logsNeverStarted: "Non è mai stato avviato, quindi non c'è ancora niente di registrato — avvialo, e i log ci saranno anche se l'avvio fallisce.",
    logsNoTemplate: 'Un template non gira mai, quindi non ha mai registrato niente.',
    logsError: 'I log non sono stati catturati',

    // Dialog standard (round #156): i tab del dettaglio condivisi dalle
    // liste CT e Template CT, più i lucchetti col perché.
    tabs: {
      general: 'Generale',
      hardware: 'Hardware',
      network: 'Rete',
      provenance: 'Provenienza'
    },
    templateFlag: 'Template CT',
    templateLock: 'Si cambia solo con le azioni Promuovi / Riporta a CT — mai una modifica diretta.',
    sourceLock: 'Provenienza, registrata alla nascita — mai riscritta; una sorgente morta lascia il riferimento nudo.',
    storageLock: 'Fissato alla creazione — lo spostamento del rootfs è un round futuro.',
    startedAt: 'Avviato',
    lastStop: 'Ultimo arresto',
    editNicsTitle: 'Modifica reti',
    nicDisconnected: 'scollegata'
  },
  ctTemplates: {
    title: 'Template CT',
    importAction: 'Importa da catalogo',
    importTitle: 'Importa template CT dal catalogo',
    importSubmit: 'Importa',
    importError: 'L\'import è stato rifiutato',
    importHint: 'Materializzare copia il rootfs sullo storage scelto — il file di catalogo resta intatto. Per aggiungere nuovi file, caricali prima dentro un catalogo.',
    sourceFile: 'File template CT',
    sourceFileHint: 'I file ct-rootfs del catalogo scelto, solo quelli pronti',
    noFiles: 'Nessun file ct-rootfs pronto in questo catalogo — caricane uno dalla pagina del catalogo',
    source: 'File di origine',
    rootfsStorage: 'Storage del rootfs',
    rootfsStorageHint: 'Dove vive l\'albero rootfs estratto — uno storage con filesystem e spazio libero sufficiente per la dimensione estratta più circa 1 GiB di margine (altrimenti l\'import fallisce).',
    resources: 'Risorse',
    cores: 'vCPU',
    memoryMiB: 'Memoria (MiB)',
    cpuLimit: 'Limite CPU',
    cpuLimitHint: 'Equivalenti di core host (es. 1.5); vuoto = senza tetto',
    cpuUncapped: 'senza tetto',
    cpuWeight: 'Peso CPU',
    hostname: 'Hostname',
    hostnameHint: 'Vuoto = il nome del CT',
    nameservers: 'Nameserver DNS',
    dnsHint: 'Separati da virgola; vuoto = eredita quelli dell\'host',
    searchDomains: 'Domini di ricerca DNS',
    dnsInherited: 'Ereditato dall\'host',
    networks: 'Reti',
    nicSlot: 'Slot NIC',
    phase: {
      materializing: 'In materializzazione',
      ready: 'Pronto',
      deleting: 'In eliminazione',
      error: 'Errore'
    },
    exportAction: 'Esporta nel catalogo',
    exportTitle: 'Esporta nel catalogo',
    exportSubmit: 'Esporta',
    exportError: 'L\'export è stato rifiutato',
    exportHint: 'Impacchetta il rootfs materializzato in un NUOVO file ct-rootfs nel catalogo scelto — il template resta intatto.',
    exportName: 'Nome del file',
    exportNameHint: 'Vuoto = <template>-export-<n>',
    markCt: {
      action: 'Riporta a CT',
      confirmTitle: "Riportare '{name}' a CT?",
      confirmBody: 'Il template lascia questa lista e torna nella lista CT, di nuovo avviabile. Finché non lo promuovi di nuovo, non si possono creare CT da esso.',
      confirm: 'Riporta a CT',
      moved: "'{name}' è di nuovo un CT — lo trovi nella lista CT."
    }
  },

  // OCI wave 1 (Annex Q — il modello tag-come-branch): un tag OCI è un branch
  // (puntatore mobile), il digest è il commit; refresh = "git fetch",
  // pull = risolvi ADESSO, scarica QUEL digest.
  oci: {
    registriesTitle: 'Registry OCI',
    imagesTitle: 'Immagini OCI',
    provider: 'Provider',
    providerDockerhub: 'Docker Hub',
    providerGhcr: 'GHCR',
    providerEcr: 'Amazon ECR',
    providerQuay: 'Red Hat Quay',
    providerGeneric: 'Generico',
    providerImmutableHint: 'Il provider è immutabile dopo la creazione — un provider diverso è un nuovo registry.',
    url: 'URL',
    urlHintPreset: 'Precompilato dal provider — sovrascrivi solo per i mirror.',
    urlHintRequired: "Obbligatorio per i registry generici: l'URL base https://.",
    urlHintEcr: 'Obbligatorio: gli endpoint ECR sono per-account/per-regione — non esiste un preset.',
    credential: 'Credenziale',
    credentialNone: 'Accesso anonimo',
    credentialHint: 'Una voce keystore utente-e-password; i worker la risolvono al momento dell’uso — mai auth file per nodo.',
    nameHint: 'Nome tipo host (docker.io, registry.intra); unico per scope, immutabile',
    addRegistry: 'Aggiungi registry',
    addRegistryTitle: 'Aggiungi registry OCI',
    verify: 'Verifica',
    verifyState: 'Verifica',
    verified: 'Verificato',
    verifyFailed: 'Fallita',
    neverVerified: 'Mai verificato',
    registryInUseError: "'{name}' ha ancora repository registrati — eliminali prima.",
    repositories: 'Repository',
    repositoriesHint: 'Registrare osserva i branch (tag OCI) — non scarica nulla. Il refresh risolve ogni branch osservato e mantiene il reflog.',
    noRepositories: 'Nessun repository registrato su questo registry.',
    registerRepository: 'Registra repository',
    repoPath: 'Percorso',
    repoPathHint: "Percorso del repository sul registry, es. 'example' o 'library/nginx'",
    branches: 'Branch',
    branchesHint: 'Branch osservati (tag OCI), separati da virgola — es. 1.1.0, 1.1.1',
    branch: 'Branch',
    branchPullHint: 'Risolto ADESSO: il pull scarica il digest a cui il branch punta oggi.',
    refresh: 'Aggiorna',
    deleteRepository: 'Elimina repository',
    reflogCount: '{n} precedenti',
    noResolutions: 'Mai aggiornato — nessuna risoluzione osservata.',
    refreshFailed: "Ultimo aggiornamento fallito ({when})",
    loadError: 'Impossibile caricare i repository.',
    reference: 'Riferimento',
    digest: 'Digest',
    digestHint: 'sha256:… — l’immagine esatta, senza branch (detached)',
    size: 'Dimensione',
    nodes: 'Nodi',
    detached: 'detached',
    detachedHint: 'Scaricata per digest, senza branch — il controllo drift non si applica.',
    pulledAt: 'Scaricata',
    pull: 'Pull',
    pullTitle: 'Pull immagine',
    pullHint: 'Pre-warming, mai un prerequisito: un pull per branch risolve ADESSO e scarica QUEL digest; un pull per digest è detached.',
    pullError: 'Il pull è stato rifiutato',
    pullMode: 'Pull per',
    pullModeBranch: 'Branch (risolvi ora)',
    pullModeDigest: 'Digest (detached)',
    pullModeBranchHint: 'Il default onesto: come apt install, rieseguito più tardi può dare un’immagine diversa.',
    pullModeDigestHint: 'Digest esatto — riproducibilità e rollback; rinuncia al controllo drift.',

    // Il pattern a due pulsanti (#88, ratifica 2026-07-30 sera): "da
    // registry" è il pull guidato esistente, "da catalogo" materializza un
    // archivio oci-image pronto (la via air-gapped).
    importFromRegistry: 'Importa da registry',
    importFromCatalog: 'Importa da catalogo',
    importCatalogTitle: 'Importa immagine OCI da catalogo',
    importCatalogHint: 'La via air-gapped: un archivio oci-image pronto (tar/tar.gz — docker-archive o oci-archive) viene materializzato nello store immagini col riferimento catalog/<catalogo>/<file>@<digest>; le app lo consumano come qualsiasi altra immagine. Gli archivi si caricano nei cataloghi — questa sezione importa e basta.',
    importCatalogFile: 'Archivio',
    importCatalogFileHint: 'I file oci-image pronti del catalogo scelto.',
    importCatalogNoFiles: 'Nessun archivio oci-image pronto in questo catalogo — caricane uno dal dettaglio catalogo.',
    importCatalogError: 'L’import è stato rifiutato',
    registry: 'Registry',
    repository: 'Repository',
    cacheMissing: 'La cache OCI di cluster non è configurata — i pull sono rifiutati finché non imposti un pool di storage.',
    setCache: 'Imposta cache',
    setCacheTitle: 'Configura la cache OCI',
    setCacheHint: 'Il pool DEFAULT a livello di cluster che ospita i blob delle immagini scaricate — uno scope può fare override col proprio pool (pagina Scope). Cambiarlo non è a caldo e non riavvia nulla, e non sposta i blob: riappaiono lazy al prossimo start/uso.',
    cachePool: 'Pool di storage',
    changeCache: 'Cambia cache',
    changeCacheTitle: 'Cambia il pool della cache OCI',
    cacheCurrent: 'Cache OCI del cluster (default): {name}',
    sectionOverview: 'Panoramica',
    sectionRepositories: 'Repository',
    sectionExplore: 'Esplora',
    exploreHint: 'Ricerca remota dal vivo sul registry — Docker Hub piena (descrizione, download, stelle), altri provider best-effort. Seleziona un risultato per leggerne la descrizione completa e sfogliarne i tag remoti; da un tag puoi aggiungere il riferimento a un repository TVirt.',
    exploreSearch: 'Cerca',
    exploreUnsupported: 'Questo provider non supporta la ricerca.',
    exploreNoResult: 'Nessun repository corrispondente.',
    exploreNoTags: 'Nessun tag corrispondente su questo repository.',
    exploreOfficial: 'Ufficiale',
    explorePulls: 'Download',
    exploreStars: 'Stelle',
    exploreUpdated: 'Aggiornato {when}',
    exploreLoadMore: 'Carica altri',
    exploreSelectHint: 'Seleziona un risultato: descrizione completa e tag remoti si caricano qui.',
    exploreNoDescription: 'Nessuna descrizione disponibile da questo provider.',
    exploreTagsTitle: 'Tag remoti',
    exploreTagFilter: 'Cerca tag',
    exploreAddToRepo: 'Aggiungi a repository',
    exploreAdded: '{path}:{tag} è ora tracciato — vedi la sezione Repository.',
    addBranch: 'Aggiungi branch',
    purgeRequiredTitle: "Eliminare '{name}' e le sue immagini?",
    purgeRequiredBody: 'Nessuna app consuma questo repository, ma esistono ancora immagini scaricate: eliminarlo rimuove anche i loro record, e i blob in cache passano al garbage collector.',
    purgeConfirm: 'Elimina anche le immagini',
    providerSelectHint: 'Seleziona il tipo di registry: la card racconta preset e uso.',
    providerDesc: {
      dockerhub: 'Il registry pubblico Docker: URL precompilato, ricerca completa con descrizioni e conteggi.',
      ghcr: 'GitHub Container Registry: URL precompilato; gli account usano un personal access token.',
      ecr: "Amazon Elastic Container Registry: endpoint per-account/per-regione — l'URL va indicato.",
      quay: 'Il registry Red Hat Quay: URL precompilato.',
      generic: "Registry self-hosted (Harbor, registry:2, …): l'URL https:// è obbligatorio."
    },

    // Dialog standard (round #156): i campi del dettaglio immagine/registry
    // che le colonne non mostravano.
    layerCount: 'Layer',
    verifiedAt: 'Ultima verifica',
    verifyOk: 'Esito',
    verifyMessage: 'Messaggio di verifica',

    // Il modello #348/R39: l’immagine È il digest — il contenuto — e registry, repository e branch sono i NOMI che lo raggiungono.
    head: 'Head',
    heads: 'Head per architettura',
    listDigest: 'Digest della lista',
    neverResolved: 'Mai risolto',
    totalSize: 'Dimensione totale',
    reflogTitle: 'Storia delle risoluzioni',
    usedBy: 'Usato da',
    usageLockfile: 'lockfile',
    usageRunning: 'in esecuzione',
    evictable: 'evizionabile',
    evict: 'Evizione dalla cache',
    evictHeadTitle: 'Evizionare {digest}?',
    evictHeadBody: 'È la risoluzione corrente di almeno un collettore: evizionarlo è legittimo — è solo cache, il branch remoto lo serve ancora — ma non deve succedere per sbaglio. Il blob è UNO: l’evizione svuota tutti i collettori che lo condividono.',
    evictConfirm: 'Evizionalo comunque',

    arch: 'Architettura',
    names: 'Nomi',
    noNames: 'nessun nome',
    views: 'View',
    lastView: 'ultima view',
    evictLastViewTitle: 'Rimuovere l’ultimo riferimento a {digest}?',
    evictLastViewBody: 'È l’ultima view su questo contenuto: togliendola l’immagine cessa di esistere su TVirt e i byte ({size}) vengono liberati. È il docker image rm del modello — non un effetto collaterale, l’operazione stessa.',
    branchesTabHint: 'I branch osservati di questo repository: per ognuno le teste di oggi — una per architettura — la cronologia dei refresh e quante delle immagini che ha nominato sono in cache. Il refresh è il «git fetch» del repository: risolve tutti i suoi branch in un colpo.',
    noBranches: 'Nessun branch osservato su questo repository.',
    stopObserving: 'Smetti di osservare il branch',
    pullBranch: "Scarica in cache la testa del branch (l'aggiornamento risolve, questo scarica)",
    pullDigest: 'Scarica in cache questo digest',
    imageCount: 'Immagini',
    deleteBranchPurgeTitle: 'Lasciare senza nome le immagini di {name}?',
    deleteBranchPurgeBody: 'Nessuna app nomina questo branch, ma alcune immagini sono raggiunte solo da lui: eliminandolo restano in cache senza nome, elencate fra le immagini del repository ed evizionabili a mano.',
    deleteBranchPurgeConfirm: 'Elimina e lascia le immagini senza nome',
    imagesTabHint: 'Le immagini di questo repository — i contenuti, cioè i digest — visibili anche senza scegliere un branch. Un’immagine è unica per cluster: i nomi che la raggiungono possono essere più di uno.',
    noImages: 'Nessuna immagine di questo repository è materializzata in cache.',
    deleteRepositoryTitle: 'Eliminare {name}?',
    deleteRepositoryBody: 'Spariscono il repository e i suoi branch, con la loro cronologia. Le immagini restano in cache: perdono soltanto questo nome. Un repository nominato da un’app non si elimina.',
    provenanceCatalog: 'catalogo',
    refreshedAt: 'Aggiornato',
    neverRefreshed: 'Mai aggiornato',
    sectionBranches: 'Branch',
    sectionImages: 'Immagini',
    repositoriesGovernedElsewhere: 'Qui i repository si guardano soltanto: registrarli, aggiornarli, osservarne i branch ed evizionarne le immagini si fa nella sezione Immagini OCI.',
    gotoImages: 'Vai a Immagini OCI'
  },

  // Pod nativi (P1): the Pod list — the template's census of instances, the
  // whole-Pod lifecycle, the scale and the delete.
  pods: {
    title: 'Pod',
    containers: 'Container',
    instances: 'Istanze',
    instancesTooltip: '{running} in esecuzione, {error} in errore',
    placement: 'Collocazione',
    spread: 'distribuzione',
    ha: 'HA',
    templateRevision: 'revisione',
    deleting: 'In cancellazione',
    empty: 'Nessun Pod — crea un\'app (un container) oppure un Pod dalla CLI (create-pod).',
    openInstances: 'Apri le istanze',
    rollingRestart: 'Riavvio a rotazione',
    scale: 'Scala',
    scaleTitle: 'Scala {name}',
    scaleHint: 'Numero di istanze (0–64): in su nasce l\'ordinale libero più basso, in giù si ritira il più alto e i suoi dati persistenti restano.',
    scaleError: 'Scalatura non riuscita',
    replicas: 'Repliche',
    deleteRunningError: '{name} è in esecuzione — fermalo prima.',
    deleteInstancesIntro: 'Tutte le {count} istanze verranno rimosse.',
    stateRunning: 'In esecuzione',
    stateStopped: 'Fermo',
    editor: {
      createTitle: 'Crea Pod',
      create: 'Crea Pod',
      editTitle: 'Modifica Pod',
      nameHint: 'DNS-1123, al massimo 58 caratteri — le istanze si chiamano <nome>-<indice>; immutabile dopo la creazione.',
      restartPolicy: 'Restart policy',
      restartPolicyHint_always: 'Il nodo riavvia ogni container uscito, con backoff esponenziale (10 s → 5 min).',
      restartPolicyHint_onFailure: 'Si riavvia solo un container uscito con codice diverso da zero; l\'uscita 0 resta a riposo.',
      restartPolicyHint_never: 'Un container uscito resta com\'è — l\'istanza finisce succeeded o failed.',
      grace: 'Grazia (s)',
      graceHint: 'Il limite SIGTERM → SIGKILL di uno stop.',
      hostname: 'Hostname',
      hostnameHint: 'Vuoto = il nome dell\'istanza.',
      hostnameDefault: 'il nome dell\'istanza',
      sharePid: 'Condividi il namespace PID fra i container',
      containersIntro: 'Gli init container girano fino al termine, in ordine, prima che i container partano; i container condividono la rete dell\'istanza (un IP, localhost fra loro).',
      initContainers: 'Init container',
      initAdd: 'Aggiungi init container',
      initHint: 'Ognuno deve uscire con 0; un fallimento porta l\'istanza in errore e il nodo lo riprova con backoff.',
      containerAdd: 'Aggiungi container',
      atLeastOne: 'Un Pod ha bisogno di almeno un container.',
      duplicateNames: 'I nomi dei container devono essere unici nel Pod (init container compresi).',
      containerName: 'Nome del container',
      containerInvalid: 'Questo container è incompleto: nome (DNS-1123), immagine, mount su volumi dichiarati, limiti e probe numerici.',
      command: 'Comando',
      commandHint: 'Sostituisce l\'entrypoint dell\'immagine — un argomento per riga, mai una stringa di shell.',
      args: 'Argomenti',
      argsHint: 'Uno per riga (il CMD dell\'immagine se vuoto).',
      mounts: 'Mount',
      mountAdd: 'Aggiungi mount',
      mountNoVolumes: 'Dichiara prima un volume nel tab Storage.',
      readOnly: 'sola lettura',
      resources: 'Limiti',
      cpus: 'CPU',
      cpusHint: 'podman --cpus (es. 1.5)',
      memory: 'Memoria (MiB)',
      memoryHint: 'podman --memory',
      health: 'Probe di vitalità',
      healthCmd: 'Comando della probe',
      healthCmdHint: 'Eseguito nel container dal nodo a ogni tick di supervisione — un argomento per riga; exit 0 = sano.',
      healthTimeout: 'Timeout (s)',
      healthRetries: 'Tentativi',
      healthRetriesHint: 'Fallimenti consecutivi prima che il nodo riavvii il container.',
      ips: 'Indirizzi statici',
      ipsHint: 'Uno per istanza, in ordine di ordinale — una scala oltre la lista viene rifiutata.',
      instance: 'istanza',
      volumesHint: 'I volumi sono del Pod; il percorso di mount è di ciascun container (tab Container).',
      spread: 'Distribuzione',
      spreadHint_preferred: 'Istanze su nodi diversi quando possibile.',
      spreadHint_required: 'Istanze su nodi diversi, altrimenti la scala rifiuta.',
      spreadHint_none: 'Decide solo il decisore.',
      ha: 'Alta disponibilità (opt-in)',
      haHint: 'Ogni pezzo persistente su storage condiviso; se il nodo muore, il decider HA ricrea le istanze su un altro membro (failover automatico, come le VM HA).',
      yes: 'sì',
      no: 'no'
    },
    detail: {
      container: 'Container',
      actions: 'Azioni',
      ready: 'Pronto',
      notReady: 'Non pronto',
      restarts: 'Riavvii',
      lastRestartAt: 'Ultimo riavvio:',
      backoffUntil: 'Riprova alle',
      stateAbsent: 'assente',
      stateRunning: 'in esecuzione',
      stateTerminated: 'terminato',
      stateWaiting: 'in attesa',
      init: 'init',
      noInstances: 'Nessuna istanza (repliche 0).',
      ha: 'HA',
      haHint: "Failover automatico: se il nodo muore, il decider HA ricrea l'istanza su un altro membro (ogni pezzo persistente su storage condiviso).",
      authority: 'Autorità (nodo)',
      homeless: 'senza autorità',
      homelessHint: "Un ripristino da checkpoint lascia fuori l'autorità (#380): nessun nodo la serve finché non scali il Pod (basta anche allo stesso numero di repliche).",
      restartContainer: 'Riavvia solo questo container (il pod resta su)',
      divergingHint: 'Lo stato voluto di questa istanza diverge dal default del Pod (PD-P19).',
      revisionLag: 'revisione {applied} applicata su {current}',
      updatedInstances: '{updated}/{desired} istanze a questa revisione'
    }
  }
}
